import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';

import initialState from 'taiwan_pcb/pcb_fabrication/data/initialState';
import defaultState from 'taiwan_pcb/pcb_fabrication/data/defaultState';
import defaultQuote from 'taiwan_pcb/pcb_fabrication/data/defaultQuote';
import { shouldForceRequote } from 'shared/utils/shouldForceRequote';
import { changeGlobalParameter } from 'shared/product_tabs/tab_project/actions/changeGlobalParameter';
import { changeParameterByName } from 'taiwan_pcb/pcb_fabrication/slice/onChangeParameter';
import { validator } from 'taiwan_pcb/pcb_fabrication/slice/validators';
import { doEmailQuote, doQuote } from 'taiwan_pcb/pcb_fabrication/slice/thunks';
import { calculateRailWidths, mustUpdatePanelSize, updatePanelSize } from 'taiwan_pcb/pcb_fabrication/slice/panelize';
import { requireCustomQuote } from 'taiwan_pcb/pcb_fabrication/slice/crossNeedCustomQuote';
import {
  isParameterNameQtyValid,
  isProductTypeParameterNameQtyValid,
} from 'shared/utils/validateQuantities';
import {
  START,
  QUOTING,
  QUOTED,
  PRICE_SELECTED,
  ERROR,
} from 'shared/data/quoteSteps';

const fabricationSlice = createSlice({
  name: 'fabrication',
  initialState,
  reducers: {
    updateValidatorFlag: (state) => {
      state.validatorFlag = !state.validatorFlag;
    },
    changeParameter: (state, action) => {
      const { name, value } = action.payload;
      changeParameterByName(state.parameters, name, value);
      calculateRailWidths(state.parameters);

      if (mustUpdatePanelSize(state.parameters, name)) {
        updatePanelSize(state.parameters);
      }

      if (isParameterNameQtyValid(name)) {
        if (!state.quantitiesChanged) state.quantitiesChanged = true;

        if (state.quote.line_items.length > 0 && state.quoteStep === START) {
          const qty_index = Number(name.split('qty')[1]) - 1;

          state.quote.line_items.forEach((li) => {
            li.quantities[qty_index] = value;
          });
        }
      }

      state.parametersChanged = shouldForceRequote(name);
      state.needCustomQuote = requireCustomQuote(state.parameters);
    },
    priceSelected: (state, action) => {
      const sp = action.payload;
      state.parameters.row_selected = sp.row_selected;
      state.parameters.column_selected = sp.column_selected;
      state.parameters.quote_config_key = sp.quote_config_key;
      state.parameters.supplier_selected = sp.supplier;
      state.parameters.vendor_selected = sp.supplier;
      state.quoteStep = PRICE_SELECTED;
    },
    deleteFabrication: (state) => {
      const { currentVariant, productToCompare } = state;
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();

      const newState = _.cloneDeep({
        ...defaultState,
        validatorFlag: false,
        currentVariant,
        productToCompare,
      });

      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.destroy = true;

      return newState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload;
        if (isProductTypeParameterNameQtyValid(name, 'assembly') &&
          !state.quantitiesChanged && state.quoteStep === START) {
          const qty_index = Number(name.split('_qty')[1]) - 1;
          state.parameters.quantities[qty_index] = value;
        }
      })
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
        state.quoteStep = QUOTING;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.quote_config_key = '';
        state.parameters.supplier_selected = '';
        state.parameters.vendor_selected = '';

        if (!quote || !quote.line_items || quote.line_items.length <= 0) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quote = defaultQuote;
        state.quoteStep = ERROR;
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state) => {
        state.loading = false;
        state.emailQuoteError = true;
      });
  },
});

export const {
  updateValidatorFlag,
  changeParameter,
  priceSelected,
  deleteFabrication,
} = fabricationSlice.actions;

export default fabricationSlice.reducer;